import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { ArrowLeft, Star, MapPin, Mail, Phone, Globe, Instagram, Linkedin, Sparkles, Activity, Calendar, MessageSquare, TrendingUp } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { StateBadge } from "@/pages/CRMPage";
import { TID } from "@/constants/testIds";
import { toast } from "sonner";

const TABS = [
    { key: "dati", label: "Dati" },
    { key: "origine", label: "Origine" },
    { key: "stato", label: "Stato" },
    { key: "timeline", label: "Timeline" },
    { key: "demo", label: "Demo" },
    { key: "webinar", label: "Webinar" },
    { key: "software", label: "Software" },
    { key: "feedback", label: "Feedback" },
];

export default function ClientDetailPage() {
    const { id } = useParams();
    const qc = useQueryClient();
    const q = useQuery({ queryKey: ["client", id], queryFn: () => api.get(`/clients/${id}`).then((r) => r.data) });

    const patch = useMutation({
        mutationFn: (body) => api.patch(`/clients/${id}`, body).then((r) => r.data),
        onSuccess: () => qc.invalidateQueries({ queryKey: ["client", id] }),
    });

    if (q.isLoading || !q.data) return <div className="text-textmuted">Caricamento…</div>;
    const { client, demos, timeline, feedbacks } = q.data;

    const setStars = (n) => patch.mutate({ stars: n });
    const setState = (s) => patch.mutate({ state: s });

    return (
        <div className="space-y-6 pv-enter">
            <div className="flex items-center gap-3">
                <Link to="/crm" className="h-8 w-8 rounded-lg hover:bg-surface2 flex items-center justify-center text-textmuted">
                    <ArrowLeft size={15} strokeWidth={1.5} />
                </Link>
                <div className="pv-eyebrow">CRM / Contatto</div>
            </div>

            <div className="pv-card p-6 relative overflow-hidden">
                <div className="absolute -top-16 -right-16 h-52 w-52 rounded-full bg-primary/[0.06] blur-3xl" />
                <div className="relative flex items-start gap-5">
                    <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary text-xl font-medium">
                        {client.first_name[0]}{client.last_name[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                            <h1 className="pv-serif text-3xl leading-tight tracking-tight">{client.first_name} {client.last_name}</h1>
                            <StateBadge state={client.state} />
                            <span className="text-[11px] pv-mono text-textmuted">Score {client.lead_score}</span>
                        </div>
                        <div className="text-[13px] text-textmuted mt-0.5">{client.studio}</div>
                        <div className="flex items-center gap-4 mt-3 text-[12px] text-textmuted flex-wrap">
                            <Info icon={MapPin} text={`${client.city}, ${client.regione}`} />
                            <Info icon={Mail} text={client.email} />
                            <Info icon={Phone} text={client.phone} />
                            {client.website && <Info icon={Globe} text={client.website} />}
                            {client.instagram && <Info icon={Instagram} text={client.instagram} />}
                            {client.linkedin && <Info icon={Linkedin} text={client.linkedin} />}
                        </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                        <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map((i) => (
                                <button key={i} onClick={() => setStars(i)} className="p-0.5">
                                    <Star size={16} strokeWidth={1.5} className={i <= client.stars ? "fill-warning text-warning" : "text-textsubtle/40 hover:text-warning/60"} />
                                </button>
                            ))}
                        </div>
                        <div className="text-[11px] text-textsubtle">Priorità · <span className="text-textmain">{client.priority}</span></div>
                    </div>
                </div>
            </div>

            <Tabs defaultValue="dati" className="w-full">
                <TabsList className="bg-transparent p-0 h-auto gap-1 flex-wrap justify-start border-b border-border/[var(--border-opacity)] rounded-none w-full">
                    {TABS.map((t) => (
                        <TabsTrigger
                            key={t.key}
                            value={t.key}
                            data-testid={TID.crmClientTab(t.key)}
                            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none px-4 py-2.5 text-[13px]"
                        >{t.label}</TabsTrigger>
                    ))}
                </TabsList>

                <TabsContent value="dati" className="mt-6"><DatiTab c={client} /></TabsContent>
                <TabsContent value="origine" className="mt-6"><OrigineTab c={client} /></TabsContent>
                <TabsContent value="stato" className="mt-6"><StatoTab c={client} setState={setState} /></TabsContent>
                <TabsContent value="timeline" className="mt-6"><TimelineTab items={timeline} /></TabsContent>
                <TabsContent value="demo" className="mt-6"><DemoTab items={demos} /></TabsContent>
                <TabsContent value="webinar" className="mt-6"><WebinarTab /></TabsContent>
                <TabsContent value="software" className="mt-6"><SoftwareTab c={client} /></TabsContent>
                <TabsContent value="feedback" className="mt-6"><FeedbackTab items={feedbacks} /></TabsContent>
            </Tabs>
        </div>
    );
}

function Info({ icon: I, text }) {
    return (
        <span className="inline-flex items-center gap-1.5 truncate max-w-full">
            <I size={12} strokeWidth={1.5} />{text}
        </span>
    );
}

function K({ label, value }) {
    return (
        <div>
            <div className="pv-eyebrow mb-1">{label}</div>
            <div className="text-[14px]">{value ?? "—"}</div>
        </div>
    );
}

function DatiTab({ c }) {
    return (
        <div className="grid md:grid-cols-2 gap-x-8 gap-y-4 pv-card p-6">
            <K label="Nome" value={c.first_name} />
            <K label="Cognome" value={c.last_name} />
            <K label="Studio / Clinica" value={c.studio} />
            <K label="Città" value={`${c.city}, ${c.regione}`} />
            <K label="Telefono" value={c.phone} />
            <K label="Email" value={c.email} />
            <K label="PEC" value={c.pec} />
            <K label="Sito web" value={c.website} />
            <K label="Instagram" value={c.instagram} />
            <K label="LinkedIn" value={c.linkedin} />
            <K label="Collaboratori" value={c.collaborators} />
            <K label="Software attuale" value={c.current_software} />
            <K label="Dimensione studio" value={c.studio_size} />
            <K label="Specializzazione" value={c.specialization} />
            <K label="Regione" value={c.regione} />
            <div className="md:col-span-2">
                <div className="pv-eyebrow mb-1">Tag</div>
                <div className="flex flex-wrap gap-1.5">
                    {(c.tags || []).map((t) => <span key={t} className="text-[11px] px-2 py-0.5 rounded-md bg-primary/[0.08] text-primary">{t}</span>)}
                </div>
            </div>
            <div className="md:col-span-2">
                <div className="pv-eyebrow mb-1">Note</div>
                <div className="text-[13.5px] text-textmain leading-relaxed">{c.notes || "—"}</div>
            </div>
        </div>
    );
}

function OrigineTab({ c }) {
    return (
        <div className="grid md:grid-cols-3 gap-4">
            <div className="pv-card p-6 md:col-span-2">
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                    <K label="Come è arrivato" value={c.source} />
                    <K label="Data primo contatto" value={new Date(c.first_contact_at).toLocaleDateString("it-IT")} />
                    <K label="Costo acquisizione" value={`€${c.acquisition_cost}`} />
                    <K label="Campagna origine" value={c.campaign_origin} />
                </div>
            </div>
            <div className="pv-card p-6">
                <div className="pv-eyebrow mb-3">Motivazioni psicologiche</div>
                <div className="space-y-2">
                    {Object.entries(c.motivations || {}).map(([k, v]) => (
                        <div key={k}>
                            <div className="flex items-center justify-between text-[11.5px] mb-1">
                                <span className="text-textmain">{k}</span>
                                <span className="text-textmuted pv-mono">{v}%</span>
                            </div>
                            <div className="h-1.5 bg-surface2 rounded-full overflow-hidden">
                                <div className="h-full bg-primary" style={{ width: `${v}%` }} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function StatoTab({ c, setState }) {
    const stages = ["Lead", "Interessato", "Demo prenotata", "Demo effettuata", "Follow-up", "In trattativa", "Cliente", "Cliente Premium", "Cliente perso", "Non interessato"];
    return (
        <div className="pv-card p-6">
            <div className="pv-eyebrow mb-3">Stato cliente</div>
            <div className="flex flex-wrap gap-2">
                {stages.map((s) => (
                    <button
                        key={s}
                        onClick={() => setState(s)}
                        className={`px-3 py-1.5 rounded-lg text-[12.5px] border transition-colors ${
                            c.state === s
                                ? "bg-primary text-primary-foreground border-primary"
                                : "border-border/[var(--border-opacity)] hover:bg-surface2 text-textmain"
                        }`}
                    >{s}</button>
                ))}
            </div>
        </div>
    );
}

function TimelineTab({ items }) {
    if (!items?.length) return <div className="text-textmuted text-[13px]">Nessun evento registrato.</div>;
    return (
        <div className="pv-card p-6">
            <ol className="space-y-4 relative border-l border-border/[var(--border-opacity)] pl-5">
                {items.map((e) => (
                    <li key={e.id} className="relative">
                        <div className="absolute -left-[26px] top-1.5 h-2.5 w-2.5 rounded-full bg-primary/60 ring-4 ring-background" />
                        <div className="text-[13px]">{e.label}</div>
                        <div className="text-[11px] text-textsubtle mt-0.5">{new Date(e.at).toLocaleString("it-IT")}</div>
                    </li>
                ))}
            </ol>
        </div>
    );
}

function DemoTab({ items }) {
    if (!items?.length) return <div className="text-textmuted text-[13px]">Nessuna demo effettuata.</div>;
    return (
        <div className="space-y-2">
            {items.map((d) => (
                <div key={d.id} className="pv-card p-5 pv-hover">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-[13.5px] font-medium">{d.presenter} · {d.duration_min} min</div>
                            <div className="text-[11.5px] text-textsubtle">{new Date(d.date).toLocaleDateString("it-IT")}</div>
                        </div>
                        <div className="text-right">
                            <div className="text-[11px] uppercase tracking-wider text-textsubtle">Prob. acquisto</div>
                            <div className="text-lg pv-mono">{d.buy_probability}%</div>
                        </div>
                    </div>
                    <div className="mt-3 grid md:grid-cols-2 gap-4 text-[12.5px]">
                        <K label="Esito" value={d.outcome} />
                        <K label="Obiezioni" value={d.objections} />
                    </div>
                    <div className="mt-3 text-[13px] text-textmain leading-relaxed">{d.feedback}</div>
                </div>
            ))}
        </div>
    );
}

function WebinarTab() {
    return <div className="pv-card p-6 text-[13px] text-textmuted">Il collegamento con i webinar frequentati apparirà quando il cliente parteciperà a un evento.</div>;
}

function SoftwareTab({ c }) {
    if (!c.license_plan) return <div className="text-textmuted text-[13px]">Il cliente non ha ancora una licenza attiva.</div>;
    return (
        <div className="grid md:grid-cols-3 gap-4">
            <div className="pv-card p-6 md:col-span-2">
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                    <K label="Piano" value={c.license_plan} />
                    <K label="Ultimo login" value={c.last_login_at && new Date(c.last_login_at).toLocaleString("it-IT")} />
                    <K label="Attivazione" value={c.activation_date && new Date(c.activation_date).toLocaleDateString("it-IT")} />
                    <K label="Rinnovo" value={c.renewal_date && new Date(c.renewal_date).toLocaleDateString("it-IT")} />
                    <K label="Login 7 giorni" value={c.logins_7d} />
                    <K label="Login 30 giorni" value={c.logins_30d} />
                    <K label="Minuti di utilizzo" value={c.usage_minutes} />
                    <K label="Adozione" value={`${c.adoption_pct}%`} />
                </div>
                <div className="mt-6 pv-eyebrow mb-2">Feature utilizzate</div>
                <div className="flex flex-wrap gap-1.5 mb-4">
                    {c.features_used?.map((f) => <span key={f} className="text-[11px] px-2 py-0.5 rounded-md bg-success/10 text-success">{f}</span>)}
                </div>
                <div className="pv-eyebrow mb-2">Feature mai usate</div>
                <div className="flex flex-wrap gap-1.5">
                    {c.features_unused?.map((f) => <span key={f} className="text-[11px] px-2 py-0.5 rounded-md bg-textsubtle/10 text-textmuted">{f}</span>)}
                </div>
            </div>
            <div className="pv-card p-6">
                <div className="pv-eyebrow mb-2">Health Score</div>
                <div className="text-5xl pv-serif text-primary">{c.health_score}</div>
                <div className="text-[11.5px] text-textmuted mb-4">/ 100</div>
                <div className="h-2.5 bg-surface2 rounded-full overflow-hidden">
                    <div className="h-full bg-primary transition-all" style={{ width: `${c.health_score}%` }} />
                </div>
                <div className="mt-4 text-[12px] text-textmuted leading-relaxed">
                    {c.health_score >= 80 ? "Cliente fidelizzato." :
                     c.health_score >= 60 ? "Cliente attivo." :
                     c.health_score >= 40 ? "Cliente da coinvolgere." :
                     c.health_score >= 20 ? "Cliente a rischio." : "Alto rischio abbandono."}
                </div>
            </div>
        </div>
    );
}

function FeedbackTab({ items }) {
    if (!items?.length) return <div className="text-textmuted text-[13px]">Nessun feedback registrato.</div>;
    return (
        <div className="space-y-2">
            {items.map((f) => (
                <div key={f.id} className="pv-card p-4 flex items-start gap-3">
                    <div className="h-8 w-8 rounded-lg bg-primary/[0.08] text-primary flex items-center justify-center flex-shrink-0">
                        <MessageSquare size={13} strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[12px] px-2 py-0.5 rounded-md bg-textsubtle/10 text-textmuted">{f.type}</span>
                            <span className="text-[11px] text-textsubtle">{new Date(f.created_at).toLocaleDateString("it-IT")}</span>
                        </div>
                        <div className="text-[13px] mt-1.5 leading-relaxed">{f.text}</div>
                    </div>
                </div>
            ))}
        </div>
    );
}
